import { SelectedInputType } from 'src/features/trends/types/selected-input';
import { ChartData, SeriesVariant } from 'src/components/streametric/lineChart/types';
import { isDiscreteDataType, isOnChangeTag } from 'src/features/trends/utils';

type ValueType = {
  [timestamp: string]: number;
};
type ReadingType = {
  displayName: string;
  rawInputName: string;
  rawUnit?: string;
  tagId: string;
  values: ValueType;
};

const BAND_VARIANTS: SeriesVariant[] = ['min', 'avg', 'max'];
const VARIANT_LABEL: Record<SeriesVariant, string> = { min: 'Min', avg: 'Avg', max: 'Max' };

const toLocalTimestamp = (date: string | Date): number => {
  const utcDate = new Date(date);
  return utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000;
};

const buildTransformedData = (values: ValueType[]): [number, number][] => {
  const removedEmptyObjects = values.filter((obj) => Object.keys(obj).length !== 0);
  const dataArray: { datetime: string; value: number }[] = [];

  for (const key in removedEmptyObjects) {
    if (Object.hasOwnProperty.call(removedEmptyObjects, key)) {
      const nestedData = removedEmptyObjects[key];
      for (const nestedKey in nestedData) {
        if (Object.hasOwnProperty.call(nestedData, nestedKey)) {
          dataArray.push({ datetime: nestedKey, value: nestedData[nestedKey] });
        }
      }
    }
  }

  return dataArray.map((entry) => [toLocalTimestamp(entry.datetime), entry.value]);
};

// On-change data only reports a point when the value actually changes, so the last
// known value is guaranteed to hold through the end of the queried range. Carrying
// it forward to endDate turns a lone point into a visible line instead of a single dot.
const extendOnChangeData = (data: [number, number][], endDate?: string): [number, number][] => {
  if (data.length === 0 || !endDate) return data;

  const [lastTimestamp, lastValue] = data[data.length - 1];
  const endTimestamp = toLocalTimestamp(endDate);

  if (endTimestamp <= lastTimestamp) return data;

  return [...data, [endTimestamp, lastValue]];
};

const buildSeriesName = (siteName: string, inputName: string, unit: string): string => {
  const displayUnit = unit && unit !== 'null' ? `(${unit})` : '';
  return siteName == null
    ? `${inputName} ${displayUnit}`.trim()
    : `${siteName} - ${inputName} ${displayUnit}`.trim();
};

export const getSeries = (
  readings: ReadingType[],
  selectedInputs: SelectedInputType[],
  endDate?: string
): ChartData[] => {
  if (readings.length === 0) return [];

  return selectedInputs.flatMap((x, index): ChartData[] => {
    const baseName = buildSeriesName(x.siteName, x.inputName, x.unit);

    // Matched by row index (not inputId) so duplicate tags with different
    // statistics don't collide with each other's data.
    if (x.statistic === 'Min-Average-Max') {
      return BAND_VARIANTS.map((variant) => {
        const filteredTags = readings?.filter((item) => item.tagId === `${index}__${variant}`);
        const values = filteredTags?.map((item: any) => item.values);
        return {
          name: `${baseName} [${VARIANT_LABEL[variant]}]`,
          data: buildTransformedData(values),
          seriesVariant: variant,
          stepped: isDiscreteDataType(x.dataType),
        };
      });
    }

    const isOnChange = isOnChangeTag(x);

    const filteredTags = readings?.filter((item) => item.tagId === `${index}`);

    const values = filteredTags?.map((item: any) => item.values);
    const data = buildTransformedData(values);

    return [
      {
        name: baseName,
        data: isOnChange ? extendOnChangeData(data, endDate) : data,
        stepped: isOnChange,
      },
    ];
  });
};
